import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { useAuth } from '../contexts/AuthContext';
import {
  Send, Loader2, Database, ChevronDown, ChevronUp,
  Users, Clock, Phone, Mail, MessageSquare, ArrowLeft, Plus,
  Brain, FolderKanban, Building, User, X, Bookmark, Star
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { ChatLexicon } from '../components/ChatLexicon';
import { ScopeCombobox } from '../components/scope/ScopeCombobox';
import { AsyncScopeCombobox } from '../components/scope/AsyncScopeCombobox';
import { LanguageCombobox } from '../components/voice/LanguageCombobox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeColors = {
  call: 'bg-blue-100 text-blue-700',
  email: 'bg-purple-100 text-purple-700',
  sms: 'bg-emerald-100 text-emerald-700',
  whatsapp: 'bg-green-100 text-green-700',
  note: 'bg-amber-100 text-amber-700',
};
const typeLabels = { call: 'Appels', email: 'Emails', sms: 'SMS', whatsapp: 'WhatsApp', note: 'Notes' };

function SourcesPanel({ sources, sourceBreakdown, collaboratorBreakdown, memoriesUsed }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="mt-1.5 ml-1" data-testid="global-sources-panel">
      <div className="flex items-center gap-1.5 flex-wrap">
        <Database className="w-3 h-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{memoriesUsed} source{memoriesUsed > 1 ? 's' : ''}</span>
        <span className="text-muted-foreground/40 text-xs">|</span>
        {Object.entries(collaboratorBreakdown || {}).map(([name, count]) => (
          <span key={name} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-indigo-100 text-indigo-700">
            <Users className="w-2.5 h-2.5" /> {name}: {count}
          </span>
        ))}
        {Object.entries(sourceBreakdown || {}).map(([type, count]) => (
          <span key={type} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium ${typeColors[type] || 'bg-muted text-muted-foreground'}`}>
            {typeLabels[type] || type}: {count}
          </span>
        ))}
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-0.5 text-xs text-primary hover:text-primary/80 ml-1" data-testid="toggle-global-sources">
          {open ? 'Masquer' : 'Voir'} {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>
      {open && (
        <div className="mt-2 space-y-1 pl-1">
          {sources?.map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground py-0.5">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium bg-indigo-50 text-indigo-600 border-indigo-200">{s.collaborator}</Badge>
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${typeColors[s.type] || 'bg-muted'}`}>{s.type}</span>
              <Clock className="w-2.5 h-2.5" /> {s.date}
              {s.contact && <span>— {s.contact}</span>}
              {s.summary && <span className="truncate max-w-[300px] opacity-70">— {s.summary}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function GlobalChatPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scope state — initialized from URL query params
  const [scope, setScope] = useState(searchParams.get('scope') || null);
  const [scopeId, setScopeId] = useState(searchParams.get('scopeId') || null);
  const [scopeName, setScopeName] = useState(searchParams.get('scopeName') || '');
  const [chatLang, setChatLang] = useState('auto');

  // Saved queries
  const [savedQueries, setSavedQueries] = useState([]);
  const [showAddQuery, setShowAddQuery] = useState(false);
  const [newQueryText, setNewQueryText] = useState('');
  const addQueryRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSessions();
    fetchSavedQueries();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/chat/global/sessions`, { headers });
      setSessions(res.data || []);
    } catch { /* ignore */ }
  };

  const fetchSavedQueries = async () => {
    try {
      const res = await axios.get(`${API}/chat/saved-queries`, { headers });
      setSavedQueries(res.data || []);
    } catch { /* ignore */ }
  };

  const handleAddQuery = async () => {
    if (!newQueryText.trim()) return;
    try {
      const res = await axios.post(`${API}/chat/saved-queries`, { question: newQueryText.trim() }, { headers });
      setSavedQueries(prev => [res.data, ...prev]);
      setNewQueryText('');
      setShowAddQuery(false);
      toast.success('Question sauvegardee');
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDeleteQuery = async (id) => {
    try {
      await axios.delete(`${API}/chat/saved-queries/${id}`, { headers });
      setSavedQueries(prev => prev.filter(q => q.id !== id));
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleScopeChange = (newScope, newScopeId, label) => {
    setScope(newScope);
    setScopeId(newScopeId);
    setScopeName(label || '');
    // Clear URL params when user changes scope manually
    if (searchParams.has('scope')) {
      setSearchParams({}, { replace: true });
    }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/chat/global/history?session_id=${sid}`, { headers });
      const msgs = (res.data || []).map(log => ([
        { role: 'user', content: log.message, ts: log.created_at },
        { role: 'assistant', content: log.response, sources: log.sources, source_breakdown: log.source_breakdown,
          collaborator_breakdown: log.collaborator_breakdown, memories_used: log.memory_count, ts: log.created_at,
          retrieval: log.retrieval },
      ])).flat();
      setMessages(msgs);
      setSessionId(sid);
      setShowSessions(false);
    } catch { toast.error('Erreur chargement session'); }
  };

  const newConversation = () => {
    setMessages([]);
    setSessionId('');
    setScope(null);
    setScopeId(null);
    setScopeName('');
    inputRef.current?.focus();
  };

  const sendMessage = async (msg) => {
    if (!msg.trim() || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const payload = {
        message: msg,
        session_id: sessionId,
        max_memories: 15,
        language: chatLang === 'auto' ? null : chatLang,
      };
      if (scope && scopeId) {
        payload.scope = scope === 'collaborator' ? 'person' : scope;
        payload.scope_id = scopeId;
        payload.scope_name = scopeName;
      }

      const res = await axios.post(`${API}/chat/global`, payload, { headers, timeout: 180000 });
      const data = res.data;
      if (data.session_id && !sessionId) setSessionId(data.session_id);

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response,
        sources: data.sources,
        source_breakdown: data.source_breakdown,
        collaborator_breakdown: data.collaborator_breakdown,
        memories_used: data.memories_used,
        response_time_ms: data.response_time_ms,
        retrieval: data.retrieval,
      }]);
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || e.message));
      setMessages(prev => [...prev, { role: 'assistant', content: 'Erreur lors du traitement.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => sendMessage(input);

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-4rem)] flex flex-col max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-background">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/clones')} data-testid="global-chat-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center">
                <Users className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold" data-testid="global-chat-title">Chat Entreprise</h2>
                <p className="text-xs text-muted-foreground">Recherche dans tous les collaborateurs</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={newConversation} data-testid="global-chat-new">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSessions(!showSessions)} data-testid="global-chat-sessions-toggle">
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> Sessions
            </Button>
          </div>
        </div>

        {/* Scope Selector */}
        <div className="flex items-center gap-2 px-4 py-2 border-b bg-muted/20 flex-wrap" data-testid="global-scope-selector">
          <span className="text-xs text-muted-foreground mr-1">Scope :</span>
          <Button
            variant={!scope ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => handleScopeChange(null, null)}
            data-testid="global-scope-all"
          >
            <Users className="w-3 h-3" /> Tous
          </Button>
          <AsyncScopeCombobox
            token={token}
            endpoint="/persons"
            extractItems={(data) => {
              const arr = Array.isArray(data) ? data : data.persons || [];
              return arr.map(p => ({ ...p, name: p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() }));
            }}
            value={scope === 'collaborator' ? scopeId : null}
            onSelect={(id, label) => id ? handleScopeChange('collaborator', id, label) : handleScopeChange(null, null)}
            placeholder="Collaborateur..."
            emptyText="Aucun collaborateur."
            labelFn={(p) => p.name}
            icon={Brain}
            testId="global-scope-person-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/contacts"
            extractItems={(data) => data.contacts || (Array.isArray(data) ? data : [])}
            value={scope === 'contact' ? scopeId : null}
            onSelect={(id, label) => id ? handleScopeChange('contact', id, label) : handleScopeChange(null, null)}
            placeholder="Contact..."
            emptyText="Aucun contact."
            labelFn={(c) => `${c.first_name || ''} ${c.last_name || ''}`.trim()}
            icon={User}
            initialLabel={scope === 'contact' ? scopeName : null}
            testId="global-scope-contact-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/dossiers"
            extractItems={(data) => Array.isArray(data) ? data : data.dossiers || []}
            value={scope === 'dossier' ? scopeId : null}
            onSelect={(id, label) => id ? handleScopeChange('dossier', id, label) : handleScopeChange(null, null)}
            placeholder="Dossier..."
            emptyText="Aucun dossier."
            labelFn={(d) => d.title || d.name || d.id}
            icon={FolderKanban}
            limitParam="limit"
            limitValue={10}
            testId="global-scope-dossier-select"
          />
          <AsyncScopeCombobox
            token={token}
            endpoint="/organizations"
            extractItems={(data) => Array.isArray(data) ? data : data.organizations || []}
            value={scope === 'organization' ? scopeId : null}
            onSelect={(id, label) => id ? handleScopeChange('organization', id, label) : handleScopeChange(null, null)}
            placeholder="Organisation..."
            emptyText="Aucune organisation."
            labelFn={(o) => o.name}
            icon={Building}
            initialLabel={scope === 'organization' ? scopeName : null}
            testId="global-scope-org-select"
          />
          <div className="ml-auto">
            <LanguageCombobox
              value={chatLang}
              onSelect={setChatLang}
              testId="global-language-select"
            />
          </div>
        </div>

        {/* Sessions panel */}
        {showSessions && sessions.length > 0 && (
          <div className="border-b bg-muted/30 px-4 py-2 space-y-1 max-h-40 overflow-auto">
            {sessions.map(s => (
              <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                className={`w-full text-left px-3 py-1.5 rounded text-xs hover:bg-muted transition ${s.session_id === sessionId ? 'bg-muted font-medium' : ''}`}
                data-testid={`session-${s.session_id}`}>
                <span className="truncate block">{s.last_message}</span>
                <span className="text-muted-foreground">{s.msg_count} msgs</span>
              </button>
            ))}
          </div>
        )}

        {/* Messages */}
        <ScrollArea className="flex-1 px-4 py-4">
          {messages.length === 0 && (
            <div className="py-4 px-2">
              {/* Saved Queries */}
              {(savedQueries.length > 0 || showAddQuery) && (
                <div className="mb-6 max-w-3xl mx-auto" data-testid="saved-queries-section">
                  <div className="flex items-center justify-between mb-2.5">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-amber-500" />
                      <h4 className="text-sm font-semibold">Mes questions recurrentes</h4>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs gap-1"
                      onClick={() => { setShowAddQuery(!showAddQuery); setTimeout(() => addQueryRef.current?.focus(), 100); }}
                      data-testid="add-saved-query-btn"
                    >
                      <Plus className="w-3 h-3" /> Ajouter
                    </Button>
                  </div>

                  {showAddQuery && (
                    <div className="flex gap-2 mb-3" data-testid="add-query-form">
                      <Input
                        ref={addQueryRef}
                        value={newQueryText}
                        onChange={e => setNewQueryText(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddQuery()}
                        placeholder="Tapez votre question recurrente..."
                        className="flex-1 h-8 text-xs"
                        data-testid="add-query-input"
                      />
                      <Button size="sm" className="h-8 text-xs" onClick={handleAddQuery} disabled={!newQueryText.trim()} data-testid="add-query-save">
                        Sauvegarder
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => { setShowAddQuery(false); setNewQueryText(''); }}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {savedQueries.map(sq => (
                      <button
                        key={sq.id}
                        onClick={() => sendMessage(sq.question)}
                        className="group relative flex items-start gap-2 text-left px-3 py-2.5 rounded-xl border bg-card hover:border-amber-300 hover:bg-amber-50/50 transition-all text-sm"
                        data-testid={`saved-query-${sq.id}`}
                      >
                        <Bookmark className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors line-clamp-2">{sq.question}</span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteQuery(sq.id); }}
                          className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-md hover:bg-destructive/10"
                          data-testid={`delete-query-${sq.id}`}
                        >
                          <X className="w-3 h-3 text-destructive" />
                        </button>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Default suggestions (ChatLexicon) */}
              <ChatLexicon
                type="global"
                onSelect={(q) => sendMessage(q)}
              />

              {/* Add query hint when no saved queries exist */}
              {savedQueries.length === 0 && !showAddQuery && (
                <div className="max-w-3xl mx-auto mt-4">
                  <button
                    onClick={() => { setShowAddQuery(true); setTimeout(() => addQueryRef.current?.focus(), 100); }}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-muted-foreground/20 text-xs text-muted-foreground hover:border-amber-300 hover:text-amber-600 transition-colors"
                    data-testid="add-first-query-btn"
                  >
                    <Star className="w-3.5 h-3.5" />
                    Ajouter une question recurrente
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${msg.role === 'user'
                  ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-2.5'
                  : 'bg-muted/50 border rounded-2xl rounded-bl-md px-4 py-3'}`}>
                  {msg.role === 'assistant' && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Users className="w-3.5 h-3.5 text-indigo-600" />
                      <span className="text-xs font-medium text-indigo-600">Chat Entreprise</span>
                      {msg.response_time_ms && (
                        <span className="text-xs text-muted-foreground ml-auto">{msg.response_time_ms}ms</span>
                      )}
                    </div>
                  )}
                  {msg.role === 'assistant' ? (
                    <div className="text-sm prose prose-sm max-w-none dark:prose-invert prose-headings:text-base prose-headings:font-semibold prose-headings:mt-3 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0" data-testid={`msg-${i}`}>
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap" data-testid={`msg-${i}`}>{msg.content}</p>
                  )}
                  {msg.role === 'assistant' && msg.sources && (
                    <SourcesPanel
                      sources={msg.sources}
                      sourceBreakdown={msg.source_breakdown}
                      collaboratorBreakdown={msg.collaborator_breakdown}
                      memoriesUsed={msg.memories_used}
                    />
                  )}
                  {msg.role === 'assistant' && msg.retrieval && msg.retrieval.mode === 'map_reduce' && (
                    <div className="mt-1.5" data-testid="global-map-reduce-badge">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-700 font-medium">
                        Synthese complete ({msg.retrieval.total_memories} interactions, {msg.retrieval.batches} lots)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted/50 border rounded-2xl rounded-bl-md px-4 py-3">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="border-t bg-background px-4 py-3">
          {scope && (
            <div className="flex items-center gap-1.5 mb-2 max-w-4xl mx-auto">
              <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium" data-testid="active-scope-badge">
                {scope === 'collaborator' ? 'Collaborateur' : scope === 'contact' ? 'Contact' : scope === 'dossier' ? 'Dossier' : 'Organisation'}: {scopeName}
              </span>
            </div>
          )}
          <div className="flex gap-2 max-w-4xl mx-auto">
            <Input
              ref={inputRef}
              data-testid="global-chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
              placeholder={scope ? `Question sur ${scopeName}...` : 'Posez une question sur tous les collaborateurs...'}
              className="flex-1"
              disabled={loading}
            />
            <Button onClick={handleSend} disabled={loading || !input.trim()} data-testid="global-chat-send">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
